import { useEffect, useRef, useState } from 'react'

import {
  Breadcrumb,
  type HeadItemType,
  Icon,
  Modal,
  Page,
  Search,
  SlimButton,
  Table,
} from 'components'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { getCustomers } from 'services/shared/customer'

import * as S from './styles'

const ICON_SIZE = '18'
const DEFAULT_PAGE = 1
const MAX_LIST_SIZE = 50

import { addKeyAccount, deleteKeyAccount, getKeyAccounts } from 'services/keyAccounts'
import type { KeyAccount } from 'services/keyAccounts/types'
import { theme } from 'styles/theme/hav-theme'

import { TrackingModal } from './components/TrackingModal'

// NOTE: This type is used to map Customer the old customer data to be used by the search component
// TODO: Remove this type when the search component is updated to use the new customer data
type CustomerSearch = {
  cd_clie: string
  nm_clie: string
  nr_cnpj_cpf: string
}

const headDataCustomers: HeadItemType[] = [
  { columnName: 'name', label: 'Razão social' },
  { columnName: 'id', label: 'Código SAP' },
  { columnName: 'cnpj', label: 'CNPJ' },
  { actionsLabel: true, columnName: '', label: '' },
]

const KeyAccounts = () => {
  const searchClientRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [openTracking, setOpenTracking] = useState(false)
  const [selectedKeyAccount, setSelectedKeyAccount] = useState<KeyAccount | null>(null)
  const [keyAccounts, setKeyAccounts] = useState<KeyAccount[]>([])

  const defaultFields: {
    customer: string
  } = {
    customer: '',
  }

  const methods = useForm({
    defaultValues: defaultFields,
  })

  const { control } = methods

  const fetchKeyAccounts = async () => {
    setLoading(true)

    try {
      const { data: keyAccounts } = await getKeyAccounts()

      setKeyAccounts(keyAccounts)
    } catch (error) {
      toast.error('Erro ao buscar clientes!')
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomers = async (inputValue: string, page: string): Promise<CustomerSearch[]> => {
    try {
      const { data: customers } = await getCustomers(
        {
          search: inputValue.toLocaleUpperCase(),
        },
        {
          page: DEFAULT_PAGE,
          perPage: MAX_LIST_SIZE,
        },
      )

      return customers.map((customer) => {
        return {
          cd_clie: customer.id,
          nm_clie: customer.name,
          nr_cnpj_cpf: customer.cnpjFormatted!,
        }
      })
    } catch (error) {
      toast.error('Erro ao buscar clientes!')
      return []
    }
  }

  const checkKeyAccountExists = () => {
    if (!selectedKeyAccount) return

    const keyAccount = keyAccounts.find((keyAccount) => keyAccount.id === selectedKeyAccount.id)

    return keyAccount
  }

  const handleAddKeyAccount = async () => {
    try {
      setShowAddModal(false)
      const keyAccount = checkKeyAccountExists()

      if (keyAccount) {
        toast.warning(
          'Não é possível adicionar o cliente, pois ele já pertence à categoria Key Accounts',
        )

        return
      }

      if (!selectedKeyAccount) return

      setLoading(true)
      await addKeyAccount(selectedKeyAccount.id)

      fetchKeyAccounts()
      toast.success('Cliente adicionado com sucesso!')
    } catch (error) {
      setLoading(false)
      toast.error('Erro ao adicionar o cliente!')
      console.error('Error adding key account:', error)
    } finally {
      setSelectedKeyAccount(null)
    }
  }

  const handleDeleteKeyAccount = async () => {
    try {
      setShowDeleteModal(false)
      const keyAccount = checkKeyAccountExists()

      if (keyAccount) {
        setLoading(true)
        if (!selectedKeyAccount) return

        await deleteKeyAccount(selectedKeyAccount.id)

        setKeyAccounts(keyAccounts.filter((keyAccount) => keyAccount.id !== selectedKeyAccount.id))

        toast.success('Cliente excluído com sucesso!')
        return
      }
    } catch (error) {
      toast.error('Erro ao excluir o cliente!')
      console.error('Error deleting key account:', error)
    } finally {
      setLoading(false)
      setSelectedKeyAccount(null)
    }
  }

  const handleOpenTracking = () => {
    setOpenTracking(true)
  }

  useEffect(() => {
    fetchKeyAccounts()
  }, [])

  const renderBody = () => {
    return keyAccounts.map((keyAccount) => {
      const key = `${keyAccount.id}`

      return {
        content: [
          <span key={`name-${key}`}>{keyAccount.name}</span>,
          <span key={`id-${key}`}>{keyAccount.id}</span>,
          <span key={`cnpj-${key}`}>{keyAccount.cnpjFormatted}</span>,
          <span key={`actions-${key}`}>
            {
              <SlimButton
                icon={<Icon icon="trash" size={'20'} />}
                iconColor={theme.colors.alert.danger[50]}
                onClick={() => {
                  setShowDeleteModal(true)
                  setSelectedKeyAccount(keyAccount)
                }}
                variant="icon"
              />
            }
          </span>,
        ],
        id: key,
      }
    })
  }

  const renderTopBar = () => (
    <S.TopBar>
      <SlimButton
        content="Tracking"
        data-testid="tracking-button"
        disabled={loading}
        icon={<Icon icon="history" size={ICON_SIZE} />}
        onClick={handleOpenTracking}
        variant="ghost"
      />
    </S.TopBar>
  )

  return (
    <>
      <Page fullwidth hasHeader loading={loading} renderHeaderComponent={renderTopBar()}>
        <Breadcrumb />
        <S.Wrapper>
          <S.Container>
            <S.Title>Key Accounts</S.Title>
            <S.Divider />

            <FormProvider {...methods}>
              <S.Form>
                <S.FieldsContainer>
                  <S.FieldWrapper
                    mediaTemplateColumns="1fr 1fr"
                    ref={searchClientRef}
                    templateColumns="1fr 1fr 1fr"
                  >
                    <S.FieldItemWrapper>
                      <Controller
                        control={control}
                        name="customer"
                        render={({ field }) => (
                          <Search
                            id={field.name}
                            isClientSearch
                            key={field.name}
                            keysColumnsOption={['nm_clie']}
                            label={'Adicionar cliente'}
                            loadOnFocus
                            loadOptions={fetchCustomers}
                            optionsLimit={MAX_LIST_SIZE}
                            optionsMaxHeight="400px"
                            page={DEFAULT_PAGE}
                            placeHolder={'Busque por código ou nome'}
                            setOptionSelected={(customer) => {
                              setSelectedKeyAccount({
                                cnpj: customer.nr_cnpj_cpf,
                                id: customer.cd_clie,
                                name: customer.nm_clie,
                              })
                              setShowAddModal(true)
                            }}
                            type="select"
                          />
                        )}
                      />
                    </S.FieldItemWrapper>
                  </S.FieldWrapper>
                </S.FieldsContainer>
              </S.Form>
            </FormProvider>

            <S.TableContainer>
              <S.TextInfo>{keyAccounts?.length} clientes totais</S.TextInfo>
              <Table bodyData={renderBody()} headData={headDataCustomers} rowHeight={40} />
            </S.TableContainer>
          </S.Container>
        </S.Wrapper>
      </Page>

      <Modal
        buttonAction={handleAddKeyAccount}
        buttonText="Adicionar"
        closeAction={() => setShowAddModal(false)}
        loading={loading}
        modalWidth={456}
        open={showAddModal}
        returnAction={() => setShowAddModal(false)}
        setOpen={setShowAddModal}
        showReturn
        title="Adicionar cliente"
      >
        <span>
          Tem certeza de que deseja adicionar o cliente <b>{selectedKeyAccount?.name}</b> à
          categoria de Key Accounts?
        </span>
      </Modal>

      <Modal
        buttonAction={handleDeleteKeyAccount}
        buttonText="Excluir"
        buttonVariant="danger"
        closeAction={() => setShowDeleteModal(false)}
        loading={loading}
        modalWidth={456}
        open={showDeleteModal}
        returnAction={() => {
          setSelectedKeyAccount(null)
          setShowDeleteModal(false)
        }}
        setOpen={setShowDeleteModal}
        showReturn
        title="Excluir Cliente"
      >
        <span>
          Tem certeza de que deseja excluir o cliente <b>{selectedKeyAccount?.name}</b> à categoria
          de Key Accounts?
        </span>
      </Modal>

      {openTracking && <TrackingModal setOpen={setOpenTracking} />}
    </>
  )
}

export default KeyAccounts
